import { useCallback, useEffect, useMemo, useState } from "react"
import type { CombinedError } from "urql"
import { useQuery } from "urql"

import {
  GetCalculatePurchaseOrderPricingDocument,
  type PurchaseOrderCalculatedTotals,
  type PurchaseOrderPricingInput,
  type PurchaseOrderPricingLineResult,
  type Query,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useSetPricing } from "@/screens/Procurement/PurchaseOrder/contexts/PurchaseOrderContext"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UseCalculatePurchaseOrderPricingData {
  getCalculatePurchaseOrderPricing: (input: PurchaseOrderPricingInput) => void
  pricingResults: { orderLines: PurchaseOrderPricingLineResult[]; totals?: PurchaseOrderCalculatedTotals | null } | null
  pricingFetching: boolean
  pricingError: CombinedError | undefined
}

export const useCalculatePurchaseOrderPricing = (): UseCalculatePurchaseOrderPricingData => {
  const setPricing = useSetPricing()
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [input, setInput] = useState<PurchaseOrderPricingInput | null>(null)

  const [{ data, fetching: pricingFetching, error: pricingError }] = useQuery<Pick<Query, "order">>({
    query: GetCalculatePurchaseOrderPricingDocument,
    variables: { input: input! },
    pause: !input?.orderLines?.length,
  })

  const pricing = data?.order?.calculatePurchaseOrderPricing

  const pricingResults = useMemo(() => {
    if (pricing?.__typename === "PurchaseOrderPricingSuccess") {
      return { orderLines: pricing.orderLines, totals: pricing.totals }
    }
    return null
  }, [pricing])

  useEffect(() => {
    if (!input) {
      return
    }

    setPricing({ calculatedPricing: pricingResults, fetching: pricingFetching })
  }, [input, pricingResults, pricingFetching, setPricing])

  useEffect(() => {
    if (pricingFetching) {
      return
    }

    const failureMessage = pricing?.__typename === "PurchaseOrderPricingFailure" ? pricing.error.message : undefined
    const message = failureMessage ?? pricingError?.graphQLErrors[0]?.message ?? pricingError?.message

    if (!message) {
      return
    }

    notify({ type: NotificationType.Error, message })
  }, [pricing, pricingError, pricingFetching, notify])

  const getCalculatePurchaseOrderPricing = useCallback((input: PurchaseOrderPricingInput) => {
    setInput(input)
  }, [])

  return {
    getCalculatePurchaseOrderPricing,
    pricingResults,
    pricingFetching,
    pricingError,
  }
}
